/**
 * useMultiPreview Hook
 *
 * Custom hook for managing multi-QR preview state including selected bays
 * and independent preview states for site, fixed bay, and super bay QR codes.
 *
 * Features:
 * - Maintains selected bay state across template changes (Requirements 5.1, 5.2)
 * - Initializes to first bay of each type on mount (Requirements 5.3, 5.4)
 * - Regenerates all visible QR code previews when settings change (Requirement 5.5)
 * - Maintains independent preview states allowing partial success (Requirement 7.3)
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 7.3
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { QrTemplate } from '../types';
import type { AdvancedOptions } from '../types/advancedOptions';
import { DEFAULT_ADVANCED_OPTIONS } from '../types/advancedOptions';
import type { FixedBay, SuperBay } from '../../site/models/domainModels';
import { getEffectiveTemplate, type PreviewConfig } from '../utils/templateModifier';
import { generateQrMatrix, calculateOverlaySize } from '../generator/qrGenerator';
import { renderQrToDataUrl } from '../generator/canvasRenderer';
import { generateSiteQrUrlWithAdvanced, generateBayQrUrlWithAdvanced } from '../utils/advancedUrlGenerator';

/**
 * Default QR code preview size in pixels.
 * Set to 512 to ensure crisp display on larger screens where preview cards
 * can be up to 320px wide. Using 2x the max display size prevents pixelation
 * when the browser scales the image.
 */
const PREVIEW_SIZE = 512;

/**
 * State for a single QR code preview
 */
export interface PreviewState {
  readonly dataUrl: string | null;
  readonly isGenerating: boolean;
  readonly error: string | null;
  readonly url: string | null;
}

/**
 * Initial preview state
 */
const INITIAL_PREVIEW_STATE: PreviewState = {
  dataUrl: null,
  isGenerating: false,
  error: null,
  url: null,
};

/**
 * Complete multi-preview state
 */
export interface MultiPreviewState {
  readonly selectedFixedBay: FixedBay | null;
  readonly selectedSuperBay: SuperBay | null;
  readonly sitePreview: PreviewState;
  readonly fixedBayPreview: PreviewState;
  readonly superBayPreview: PreviewState;
}

/**
 * Return type for the useMultiPreview hook
 */
export interface UseMultiPreviewResult {
  readonly state: MultiPreviewState;
  readonly setSelectedFixedBay: (bay: FixedBay) => void;
  readonly setSelectedSuperBay: (bay: SuperBay) => void;
}

/**
 * Extracts bay label from a FixedBay
 * Uses the bay's actual ID as the display label
 */
function getBayLabel(bay: FixedBay): string {
  return bay.id;
}

/**
 * Generates a QR code for a given preview configuration
 *
 * @param template - The base QR template
 * @param previewConfig - Configuration for the preview type
 * @param url - The URL to encode in the QR code
 * @returns Promise resolving to the data URL or throwing an error
 */
async function generatePreviewQrCode(
  template: QrTemplate,
  previewConfig: PreviewConfig,
  url: string
): Promise<string> {
  // Apply effective template transformation
  const effectiveTemplate = getEffectiveTemplate(template, previewConfig);

  // Generate QR matrix
  const matrix = generateQrMatrix(url, 'H');

  // Calculate overlay size if center customization is enabled
  const overlaySize = effectiveTemplate.centerEnabled
    ? calculateOverlaySize(PREVIEW_SIZE)
    : 0;

  // Determine bay number for center label (only for fixed bays with center enabled)
  const bayNumber = effectiveTemplate.showCenterLabel
    ? previewConfig.bayNumber
    : undefined;

  // Determine logo (only show if effective template says so)
  const logoDataUrl = effectiveTemplate.showLogo
    ? effectiveTemplate.logoDataUrl
    : null;

  // Render QR code to data URL
  const dataUrl = await renderQrToDataUrl(matrix, PREVIEW_SIZE, effectiveTemplate.color, {
    centerEnabled: effectiveTemplate.centerEnabled,
    overlaySize,
    logoDataUrl,
    bayNumber,
    fontFamily: effectiveTemplate.fontFamily,
    fontSize: effectiveTemplate.fontSize,
    bayLabelColor: effectiveTemplate.bayLabelColor,
    centerPadding: effectiveTemplate.centerPadding,
    cornerLabelText: effectiveTemplate.cornerLabelText,
    moduleCount: matrix.size,
    bottomLogoEnabled: effectiveTemplate.bottomLogo,
  });

  return dataUrl;
}

/**
 * Custom hook for managing multi-QR preview state.
 *
 * This hook manages:
 * - Selected bay state for fixed and super bays
 * - Independent preview states (dataUrl, isGenerating, error) for each preview type
 * - QR code generation when template or selected bays change
 *
 * @param template - The current QR template with customization settings
 * @param siteId - The site identifier
 * @param siteName - The site name (used for corner label on site QR codes)
 * @param fixedBays - Array of fixed bays for the site
 * @param superBays - Array of super bays for the site
 * @param ensureKey - Function to ensure a valid site key exists
 * @param advancedOptions - Optional advanced options for URL generation
 * @returns Object containing state and bay selection functions
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 6.1, 6.4, 7.3
 */
export function useMultiPreview(
  template: QrTemplate,
  siteId: string,
  siteName: string,
  fixedBays: readonly FixedBay[],
  superBays: readonly SuperBay[],
  ensureKey: () => Promise<string>,
  advancedOptions?: AdvancedOptions
): UseMultiPreviewResult {
  // Use default advanced options if not provided
  const effectiveAdvancedOptions = advancedOptions ?? DEFAULT_ADVANCED_OPTIONS;
  // Selected bay state - initialized to first bay of each type (Requirements 5.3, 5.4)
  const [selectedFixedBay, setSelectedFixedBay] = useState<FixedBay | null>(
    fixedBays.length > 0 ? fixedBays[0] : null
  );
  const [selectedSuperBay, setSelectedSuperBay] = useState<SuperBay | null>(
    superBays.length > 0 ? superBays[0] : null
  );

  // Preview states - independent for each preview type (Requirement 7.3)
  const [sitePreview, setSitePreview] = useState<PreviewState>(INITIAL_PREVIEW_STATE);
  const [fixedBayPreview, setFixedBayPreview] = useState<PreviewState>(INITIAL_PREVIEW_STATE);
  const [superBayPreview, setSuperBayPreview] = useState<PreviewState>(INITIAL_PREVIEW_STATE);

  // Track if this is the initial mount to handle bay initialization
  const isInitialMount = useRef(true);

  // Update selected bays when bay arrays change (e.g., on initial load)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // Initialize to first bay of each type (Requirements 5.3, 5.4)
      if (fixedBays.length > 0 && selectedFixedBay === null) {
        setSelectedFixedBay(fixedBays[0]);
      }
      if (superBays.length > 0 && selectedSuperBay === null) {
        setSelectedSuperBay(superBays[0]);
      }
    }
  }, [fixedBays, superBays, selectedFixedBay, selectedSuperBay]);

  // Generate site QR code when template or advanced options change (Requirement 5.5, 6.1, 6.4)
  useEffect(() => {
    let isCancelled = false;

    const generateSitePreview = async (): Promise<void> => {
      setSitePreview((prev) => ({ ...prev, isGenerating: true, error: null }));

      try {
        const siteKey = await ensureKey();
        if (isCancelled) return;

        const url = generateSiteQrUrlWithAdvanced(siteId, siteKey, effectiveAdvancedOptions);
        const previewConfig: PreviewConfig = { type: 'site', siteName };

        const dataUrl = await generatePreviewQrCode(template, previewConfig, url);
        if (isCancelled) return;

        setSitePreview({ dataUrl, isGenerating: false, error: null, url });
      } catch (error) {
        if (isCancelled) return;
        const errorMessage = error instanceof Error ? error.message : 'Failed to generate site QR code';
        setSitePreview({ dataUrl: null, isGenerating: false, error: errorMessage, url: null });
      }
    };

    generateSitePreview();

    return () => {
      isCancelled = true;
    };
  }, [template, siteId, siteName, ensureKey, effectiveAdvancedOptions]);

  // Generate fixed bay QR code when template, selected bay, or advanced options change (Requirements 5.1, 5.5, 6.1, 6.4)
  useEffect(() => {
    let isCancelled = false;

    const generateFixedBayPreview = async (): Promise<void> => {
      if (!selectedFixedBay) {
        setFixedBayPreview(INITIAL_PREVIEW_STATE);
        return;
      }

      setFixedBayPreview((prev) => ({ ...prev, isGenerating: true, error: null }));

      try {
        const siteKey = await ensureKey();
        if (isCancelled) return;

        const url = generateBayQrUrlWithAdvanced(siteId, selectedFixedBay.id, siteKey, effectiveAdvancedOptions);
        const bayNumber = getBayLabel(selectedFixedBay);
        const previewConfig: PreviewConfig = {
          type: 'fixed-bay',
          bayId: selectedFixedBay.id,
          bayNumber,
        };

        const dataUrl = await generatePreviewQrCode(template, previewConfig, url);
        if (isCancelled) return;

        setFixedBayPreview({ dataUrl, isGenerating: false, error: null, url });
      } catch (error) {
        if (isCancelled) return;
        const errorMessage = error instanceof Error ? error.message : 'Failed to generate fixed bay QR code';
        setFixedBayPreview({ dataUrl: null, isGenerating: false, error: errorMessage, url: null });
      }
    };

    generateFixedBayPreview();

    return () => {
      isCancelled = true;
    };
  }, [template, siteId, selectedFixedBay, fixedBays, ensureKey, effectiveAdvancedOptions]);

  // Generate super bay QR code when template, selected bay, or advanced options change (Requirements 5.2, 5.5, 6.1, 6.4)
  useEffect(() => {
    let isCancelled = false;

    const generateSuperBayPreview = async (): Promise<void> => {
      if (!selectedSuperBay) {
        setSuperBayPreview(INITIAL_PREVIEW_STATE);
        return;
      }

      setSuperBayPreview((prev) => ({ ...prev, isGenerating: true, error: null }));

      try {
        const siteKey = await ensureKey();
        if (isCancelled) return;

        const url = generateBayQrUrlWithAdvanced(siteId, selectedSuperBay.id, siteKey, effectiveAdvancedOptions);
        const previewConfig: PreviewConfig = {
          type: 'super-bay',
          bayId: selectedSuperBay.id,
          bayName: selectedSuperBay.name,
        };

        const dataUrl = await generatePreviewQrCode(template, previewConfig, url);
        if (isCancelled) return;

        setSuperBayPreview({ dataUrl, isGenerating: false, error: null, url });
      } catch (error) {
        if (isCancelled) return;
        const errorMessage = error instanceof Error ? error.message : 'Failed to generate super bay QR code';
        setSuperBayPreview({ dataUrl: null, isGenerating: false, error: errorMessage, url: null });
      }
    };

    generateSuperBayPreview();

    return () => {
      isCancelled = true;
    };
  }, [template, siteId, selectedSuperBay, ensureKey, effectiveAdvancedOptions]);

  // Bay selection handlers - these persist across template changes (Requirements 5.1, 5.2)
  const handleSetSelectedFixedBay = useCallback((bay: FixedBay): void => {
    setSelectedFixedBay(bay);
  }, []);

  const handleSetSelectedSuperBay = useCallback((bay: SuperBay): void => {
    setSelectedSuperBay(bay);
  }, []);

  return {
    state: {
      selectedFixedBay,
      selectedSuperBay,
      sitePreview,
      fixedBayPreview,
      superBayPreview,
    },
    setSelectedFixedBay: handleSetSelectedFixedBay,
    setSelectedSuperBay: handleSetSelectedSuperBay,
  };
}
